import { LineItem, Order, OrderCapture, OrderAuthorization, OrderRefund, OrderCancellation } from '../types/orders';
import { AuthorizationGroup, LineIdAmount } from './types';

export const groupItems = (items: LineItem[]): LineItem[] => {
    // We only care about "reversed" related items for now, and move them so they come right after the
    // item they reverse.
    // Note that the related item of an item does not necessarily exist in the draft order, as it could refer to
    // an item in another Draft/Order.

    // Bubble all none related items (or non included items up), but keep initial order as much as possible
    const lineIds = items.map((item) => item.line_id || -1);
    const [parentItems, reversalItemsByParent] = items.reduce<[LineItem[], { [key: number]: LineItem[] }]>(
        ([parents, children], item) => {
            const itemIsNotRelated = !item.related_item;
            const itemIsNotReversal = item.related_item?.type !== 'reversed';
            const reversedItemIsNotLocallyPresent =
                item.related_item?.type === 'reversed' && !lineIds.includes(item.related_item?.line_id || -1);

            if (itemIsNotRelated || itemIsNotReversal || reversedItemIsNotLocallyPresent) {
                return [[...parents, item], children];
            }

            return [
                parents,
                {
                    ...children,
                    [item.related_item?.line_id || -1]: [...(children[item.related_item?.line_id || -1] || []), item],
                },
            ];
        },
        [[], {}]
    );

    // append reversal items directly under the item they are related to
    Object.entries(reversalItemsByParent).forEach(([parentId, relatedItems]) => {
        const insertAfter = parentItems.findIndex((item) => item.line_id === Number(parentId));
        parentItems.splice(insertAfter + 1, 0, ...relatedItems);
    });

    return parentItems;
};

export const getOperationAmount = (operation: OrderCapture | OrderAuthorization | OrderRefund | OrderCancellation) => {
    return operation.amount || (operation.items || []).reduce((sum, item) => sum + item.amount || 0, 0);
};

export const getOperationItemAmount = (
    operation: OrderCapture | OrderAuthorization | OrderRefund | OrderCancellation,
    lineId: number
) => {
    return (operation.items || []).filter((item) => item.line_id === lineId).reduce((sum, item) => sum + item.amount, 0);
};

// operation amounts

export const getCanceledAmount = (order: Order): number => {
    return (order.cancellations || []).reduce(
        (acc, operation) => acc + (((operation.success !== false) !== false && getOperationAmount(operation)) || 0),
        0
    );
};

export const getAuthorizedAmount = (order: Order): number => {
    const amount = (order.authorizations || []).reduce(
        (acc, operation) => acc + (((operation.success !== false) !== false && getOperationAmount(operation)) || 0),
        0
    );
    return amount;
};

export const getAuthorizableAmount = (order: Order): number => {
    const authorizedTotal = (order.net_amount || 0) - getAuthorizedAmount(order);
    const amount = (authorizedTotal === 0 && order.authorizations?.length === order.items?.length) ? authorizedTotal : authorizedTotal + getCanceledAmount(order);
    return amount;
};

export const getCapturedAmount = (order: Order): number => {
    return (order.captures || []).reduce(
        (acc, operation) => acc + (((operation.success !== false) !== false && getOperationAmount(operation)) || 0),
        0
    );
};

export const getCapturableAmount = (order: Order): number => {
    return getAuthorizedAmount(order) - getCapturedAmount(order) - getCanceledAmount(order);
};

export const getRefundedAmount = (order: Order): number => {
    return (order.refunds || []).reduce(
        (acc, operation) => acc + (((operation.success !== false) !== false && getOperationAmount(operation)) || 0),
        0
    );
};

export const getRefundableAmount = (order: Order): number => {
    return getCapturedAmount(order) - getRefundedAmount(order);
};

export const getCancelableAmount = (order: Order): number => {
    // cancel is only allowed on authorizations that do not have any other operations
    const groups = groupOperationsByAuthorizations(order);
    const cancelableGroups = groups.filter((group) => {
        const notCaptured = group.captures.length === 0;
        const notCancelled = group.cancellations.length === 0;
        return notCaptured && notCancelled;
    });
    return cancelableGroups.reduce(
        (sum, group) => sum + group.items.reduce((groupSum, item) => groupSum + (item.net_amount || 0), 0),
        0
    );
};

// item helpers

export const getItemAuthorizedAmount = (order: Order, lineId: number): number => {
    const amount = (order.authorizations || []).reduce(
        (acc, operation) =>
            acc + (((operation.success !== false) !== false && getOperationItemAmount(operation, lineId)) || 0),
        0
    );
    return amount;
};

export const getItemCapturedAmount = (order: Order, lineId: number): number => {
    return (order.captures || []).reduce(
        (acc, operation) =>
            acc + (((operation.success !== false) !== false && getOperationItemAmount(operation, lineId)) || 0),
        0
    );
};

export const getItemRefundedAmount = (order: Order, lineId: number): number => {
    return (order.refunds || []).reduce(
        (acc, operation) =>
            acc + (((operation.success !== false) !== false && getOperationItemAmount(operation, lineId)) || 0),
        0
    );
};

export const getItemCanceledAmount = (order: Order, lineId: number): number => {
    return (order.cancellations || []).reduce(
        (acc, operation) =>
            acc + (((operation.success !== false) !== false && getOperationItemAmount(operation, lineId)) || 0),
        0
    );
};

export const getOrderItem = (order: Order | AuthorizationGroup, lineId: number): LineItem => {
    const item = (order.items || []).find((item) => item.line_id === lineId);
    if (item) {
        return item;
    }
    throw new Error('order item not found');
};

// eg. {1: 1, 3: 3} + {1: 2, 2:2} = {1: 3, 2:2, 3:3}
const sumDicts = (a: LineIdAmount, b: LineIdAmount): LineIdAmount => {
    return Object.entries(a).reduce(
        (acc, [lineId, amount]) => ({
            ...acc,
            [lineId]: (acc[parseInt(lineId)] || 0) + amount,
        }),
        b
    );
};

// eg. {1: 1, 2: 2, 3: 3} - {1: 1, 2:1, 4:4} = {2:1, 3:3}
const leftSubtractDicts = (a: LineIdAmount, b: LineIdAmount): LineIdAmount => {
    return Object.entries(b).reduce((acc, [lineId, amount]) => {
        const result = { ...acc };
        const diffAmount = (result[parseInt(lineId)] || 0) - amount;
        if (diffAmount > 0) {
            result[parseInt(lineId)] = diffAmount;
        } else {
            // remove zero and negative items
            delete result[parseInt(lineId)];
        }
        return result;
    }, a);
};

// use sum dicts on all items in a single operation (since the api supports multiple operation items  to the same line_id)
const getOperationDict = <T extends OrderAuthorization & OrderCapture & OrderRefund & OrderCancellation>(
    orderCapture: T
) => {
    return (orderCapture.items || []).reduce<LineIdAmount>((acc, item) => sumDicts(acc, { [item.line_id]: item.amount }), {});
};

// use sum dicts on all operations of a given type
const getOperationsDict = <T extends OrderAuthorization & OrderCapture & OrderRefund & OrderCancellation>(
    operations: T[]
) => {
    const dicts = operations
        .filter((operation) => operation.success !== false)
        .map((operation) => getOperationDict<T>(operation));
    return dicts.reduce((acc, elem) => sumDicts(acc, elem), {});
};

const getLineItemsLineIdAmountDict = (items: LineItem[]): LineIdAmount =>
    items.reduce((acc, item) => sumDicts(acc, { [item.line_id || -1]: item.net_amount || 0 }), {});

const getUpdatedLineItems = (order: Order | AuthorizationGroup, amountDict: LineIdAmount): LineItem[] =>
    Object.entries(amountDict).map(([lineId, amount]) => {
        const item = { ...getOrderItem(order, parseInt(lineId)) };
        item.net_amount = amount;
        return item;
    });

const operationHasItems = (items: LineItem[], operation: OrderCapture & OrderRefund & OrderCancellation) => {
    return (operation.items || []).reduce(
        (result, operationItem) => result && items.find((item) => item.line_id === operationItem.line_id) !== undefined,
        true
    );
};

export const groupOperationsByAuthorizations = (order: Order): AuthorizationGroup[] => {
    // retrieve authorizations on an order that has
    return (order.authorizations || []).map((authorization) => {
        const items = (authorization.items || []).map((i) => getOrderItem(order, i.line_id));
        const captures = (order.captures || []).filter((operation) => operationHasItems(items, operation));
        const refunds = (order.refunds || []).filter((operation) => operationHasItems(items, operation));
        const cancellations = (order.cancellations || []).filter((operation) => operationHasItems(items, operation));
        return {
            items,
            authorization,
            captures,
            refunds,
            cancellations,
        };
    });
};

// operation items
export const getAuthorizeableItems = (order: Order): LineItem[] => {
    const orderItemsAmountDict = getLineItemsLineIdAmountDict(order.items || []);
    const authorizedItemsAmountDict = getOperationsDict(order.authorizations || []);
    const canceledItemsAmountDict = getOperationsDict(order.cancellations || []);
    const authorizedNotCancelled = leftSubtractDicts(authorizedItemsAmountDict, canceledItemsAmountDict);
    const authorizeableAmountDict = leftSubtractDicts(orderItemsAmountDict, authorizedNotCancelled);
    return getUpdatedLineItems(order, authorizeableAmountDict);
};

export const getCaptureableItems = (group: AuthorizationGroup): LineItem[] => {
    const authorizedItemsDict = getOperationsDict([group.authorization]);
    const capturedOperationDict = getOperationsDict(group.captures || []);
    const canceledOperationDict = getOperationsDict(group.cancellations || []);
    const notCaptured = leftSubtractDicts(authorizedItemsDict, capturedOperationDict);
    const notCapturedNotCanceled = leftSubtractDicts(notCaptured, canceledOperationDict);
    return getUpdatedLineItems(group, notCapturedNotCanceled);
};

export const getRefundableItems = (group: AuthorizationGroup): LineItem[] => {
    const capturedOperationDict = getOperationsDict(group.captures || []);
    const refundedOperationDict = getOperationsDict(group.refunds || []);
    const notRefunded = leftSubtractDicts(capturedOperationDict, refundedOperationDict);
    return getUpdatedLineItems(group, notRefunded);
};

export const getCancelableItems = (group: AuthorizationGroup): LineItem[] => {
    // An authorization group can only be cancelled if there are no
    // captures and no cancellations in the group.
    if (group.cancellations.length > 0 || group.captures.length > 0) {
        return [];
    }
    return getCaptureableItems(group);
};

export const getSignificationAuthorizationGroupsForCapture = (order: Order) => {
    const groups = groupOperationsByAuthorizations(order);
    return groups.filter((group) => {
        const operationItems = getCaptureableItems(group);
        return operationItems.length > 0;
    });
};

export const getSignificationAuthorizationGroupsForRefund = (order: Order) => {
    const groups = groupOperationsByAuthorizations(order);
    return groups.filter((group) => {
        const operationItems = getRefundableItems(group);
        return operationItems.length > 0;
    });
};

export const getSignificationAuthorizationGroupsForCancel = (order: Order) => {
    const groups = groupOperationsByAuthorizations(order);
    return groups.filter((group) => {
        const operationItems = getCancelableItems(group);
        return operationItems.length > 0;
    });
};
